/**
 * Storage schema versions and migrations of stored documents (design D9).
 *
 * The storage schema version lives in the document `meta/schema` (SchemaDocument): the version the stored documents
 * have and the list of migrations applied to them (id, version, when, how many documents they changed). A store
 * without it has version 0 (data of backends older than this module, or an empty store).
 *
 * runMigrations (BackendCore.create runs it after storage.open() and before the core serves anything, so the seed
 * script and every other tool that creates a core migrate too) applies every registered migration with a version
 * above the stored one, in version order, and records each one right after it ran. Rules for a migration:
 *  - `version` strictly increasing in MIGRATIONS (never renumber or remove a released one; add new ones at the end);
 *  - idempotent: running it again over its own output changes nothing (a crash between a migration and its record
 *    runs it again at the next start);
 *  - it works through the StorageAdapter only (no files, no SQL): documents that must change together are written
 *    with one `batch` (e.g. an account and its profile), independent documents with plain puts;
 *  - it returns the number of documents it changed (logged and recorded).
 * A store with a NEWER version than the code knows (a data directory of a newer server) is refused by the server
 * (StorageSchemaError: the old code would misread or damage the newer documents); the in-process browser backend only
 * warns and serves (an older cached page must not lock the player out of the offline game).
 *
 * Documents keep their own format markers where they already have one (the profile's `schemaVersion`, the game
 * state's `format`); the storage schema version covers the shape of the server records around them.
 */
import type { Clock } from '../../shared/clock';
import { isPlainObject } from '../../shared/json';
import { type RandomSource, randomInt } from '../../shared/random';
import type { BackendConfig } from './config';
import type { Logger } from './logger';
import { type AccountRecord, type ProfileRecord, C } from './services/deps';
import { type StorageAdapter, type StorageBatchOp, putOp } from './storage';

/** Collection of the storage's own metadata documents. */
export const META_COLLECTION = 'meta';
/** Id of the schema document in META_COLLECTION. */
export const SCHEMA_DOC_ID = 'schema';

export interface AppliedMigration {
  version: number;
  id: string;
  /** Epoch ms (the backend clock) when the migration finished. */
  at: number;
  /** Documents the migration changed. */
  changed: number;
}

/** The `meta/schema` document. */
export interface SchemaDocument {
  version: number;
  /** Every migration applied to this store, oldest first. */
  applied: AppliedMigration[];
  createdAt: number;
  updatedAt: number;
}

export interface MigrationContext {
  storage: StorageAdapter;
  config: BackendConfig;
  clock: Clock;
  rng: RandomSource;
  logger: Logger;
}

export interface Migration {
  /** The schema version of the store after this migration. */
  version: number;
  /** Stable name (kebab-case), recorded in meta/schema. */
  id: string;
  description: string;
  /** Idempotent; returns the number of documents it changed. */
  up(ctx: MigrationContext): Promise<number>;
}

export interface MigrationReport {
  /** Schema version found in the store (0 = none). */
  from: number;
  /** Schema version after the run. */
  to: number;
  /** Migrations applied by this run (empty: the store was current). */
  applied: AppliedMigration[];
}

/** The store's schema is newer than this backend's registry. */
export class StorageSchemaError extends Error {
  constructor(
    message: string,
    readonly found: number,
    readonly supported: number,
  ) {
    super(message);
    this.name = 'StorageSchemaError';
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Migrations

/**
 * v1: profile documents of stage 1 backends (before the server-owned state) get the server fields of the current
 * record explicitly: `serverState: {}` and `journal: []` (the services read a missing field as these values), and a
 * profile without a numeric `schemaVersion` gets 1. `fresh` is not added: an older record without it is fresh only
 * when it has no game state (isFreshProfile), which stays true.
 */
export const profileRecordFields: Migration = {
  version: 1,
  id: 'profile-record-fields',
  description: 'profile documents get serverState {} and journal [] where missing',
  async up({ storage }) {
    let changed = 0;
    for (const rec of await storage.list<ProfileRecord>(C.profiles)) {
      if (!isPlainObject(rec) || !isPlainObject(rec.profile) || typeof rec.profile.id !== 'string') continue;
      let dirty = false;
      if (!isPlainObject(rec.serverState)) {
        rec.serverState = {};
        dirty = true;
      }
      if (!Array.isArray(rec.journal)) {
        rec.journal = [];
        dirty = true;
      }
      if (typeof rec.profile.schemaVersion !== 'number') {
        rec.profile.schemaVersion = 1;
        dirty = true;
      }
      if (!dirty) continue;
      await storage.put(C.profiles, rec.profile.id, rec);
      changed++;
    }
    return changed;
  },
};

const NAME_ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * v2: guests of stage 1 backends all got the same display name (config.auth.defaultDisplayName, "Игрок"); since
 * stage 2 every guest gets a unique `<prefix>-XXXX` (design D7: players must be distinguishable in the tops). Every
 * guest account (no username) still named exactly the old default gets a unique default name, recorded in
 * C.defaultNames like the ones AuthService issues; the account, its profile document (displayName, team snapshot) and
 * the name record are written in ONE batch. Registered accounts keep the name they chose.
 */
export const uniqueDefaultNames: Migration = {
  version: 2,
  id: 'unique-default-names',
  description: 'guest accounts named exactly the old shared default name get a unique default name',
  async up({ storage, config, clock, rng }) {
    const prefix = config.auth.defaultDisplayName;
    let changed = 0;
    for (const a of await storage.list<AccountRecord>(C.accounts)) {
      if (!isPlainObject(a) || typeof a.playerId !== 'string' || a.displayName !== prefix || (a.username ?? null) !== null) continue;
      const now = clock.now();
      let name: string | null = null;
      for (let attempt = 0; attempt < 64 && name === null; attempt++) {
        let suffix = '';
        for (let i = attempt < 16 ? 4 : 6; i > 0; i--) suffix += NAME_ALPHABET[randomInt(rng, NAME_ALPHABET.length)];
        const candidate = `${prefix}-${suffix}`;
        if (!(await storage.get(C.defaultNames, candidate.toLowerCase()))) name = candidate;
      }
      if (name === null) continue;
      const ops: StorageBatchOp[] = [putOp(C.defaultNames, name.toLowerCase(), { name, at: now }), putOp(C.accounts, a.playerId, { ...a, displayName: name, updatedAt: now })];
      const rec = await storage.get<ProfileRecord>(C.profiles, a.playerId);
      if (rec && isPlainObject(rec.profile)) {
        rec.profile.displayName = name;
        if (rec.team) rec.team.displayName = name;
        ops.push(putOp(C.profiles, a.playerId, rec));
      }
      await storage.batch(ops);
      changed += ops.length;
    }
    return changed;
  },
};

/**
 * v3: the 14 fixed clans of the original (CLANS_DEF; a clan was an index 1..14 in the configuration, membership the
 * account's `clanId`) are gone: clans are made by players now, and membership lives only in C.clanPlayers
 * (services/clans.ts). Every account loses its `clanId` field (whatever its value; only changed accounts are written,
 * each with its own put: nothing else changes with it), and a zone whose stored `ownerClanId` is not null gets null
 * (the assault service reads the owner's clan from the clan directory whenever it reads a zone). Creates nothing.
 */
export const dropDefaultClans: Migration = {
  version: 3,
  id: 'drop-default-clans',
  description: 'accounts lose clanId and zones their ownerClanId of the fixed clans of the original',
  async up({ storage }) {
    let changed = 0;
    for (const a of await storage.list<Record<string, unknown>>(C.accounts)) {
      if (!isPlainObject(a) || typeof a.playerId !== 'string' || !('clanId' in a)) continue;
      const { clanId: _dropped, ...rest } = a;
      await storage.put(C.accounts, a.playerId, rest);
      changed++;
    }
    for (const z of await storage.list<{ zone?: { zone?: unknown; ownerClanId?: unknown; ownerClan?: unknown } }>(C.zones)) {
      const zone = isPlainObject(z) && isPlainObject(z.zone) ? z.zone : null;
      if (!zone || typeof zone.zone !== 'number' || ((zone.ownerClanId ?? null) === null && zone.ownerClan === undefined)) continue;
      const { ownerClan: _brief, ...kept } = zone;
      await storage.put(C.zones, String(zone.zone), { ...z, zone: { ...kept, ownerClanId: null } });
      changed++;
    }
    return changed;
  },
};

/** The registry, in version order. Append new migrations; never change or remove a released one. */
export const MIGRATIONS: readonly Migration[] = [profileRecordFields, uniqueDefaultNames, dropDefaultClans];

/** Schema version of the stores this backend writes. */
export const STORAGE_SCHEMA_VERSION = MIGRATIONS[MIGRATIONS.length - 1].version;

// ---------------------------------------------------------------------------------------------------------------
// Runner

/** Throws when versions are not strictly increasing positive integers or ids repeat. */
export function assertRegistry(migrations: readonly Migration[]): void {
  let last = 0;
  const ids = new Set<string>();
  for (const m of migrations) {
    if (!Number.isSafeInteger(m.version) || m.version <= last) throw new Error(`migration ${m.id}: version ${m.version} must be an integer above ${last}`);
    if (!/^[a-z0-9][a-z0-9-]{0,63}$/.test(m.id) || ids.has(m.id)) throw new Error(`migration id "${m.id}" is invalid or repeated`);
    ids.add(m.id);
    last = m.version;
  }
}

function isSchemaDocument(v: unknown): v is SchemaDocument {
  return isPlainObject(v) && Number.isSafeInteger(v.version) && Array.isArray(v.applied);
}

/** The store's schema document (null: none yet). */
export async function readSchema(storage: StorageAdapter): Promise<SchemaDocument | null> {
  const doc = await storage.get<SchemaDocument>(META_COLLECTION, SCHEMA_DOC_ID);
  if (doc === null) return null;
  if (!isSchemaDocument(doc)) throw new StorageSchemaError('The storage schema document meta/schema is malformed', -1, STORAGE_SCHEMA_VERSION);
  return doc;
}

export interface RunMigrationsOptions {
  /** Registry to run (default MIGRATIONS). */
  migrations?: readonly Migration[];
  /** A store newer than the registry: 'fail' (default; StorageSchemaError) or 'warn' (log and leave it as it is). */
  onNewer?: 'fail' | 'warn';
}

/** Brings the store to the registry's latest version (see the header). */
export async function runMigrations(ctx: MigrationContext, opts: RunMigrationsOptions = {}): Promise<MigrationReport> {
  const migrations = opts.migrations ?? MIGRATIONS;
  assertRegistry(migrations);
  const latest = migrations.length > 0 ? migrations[migrations.length - 1].version : 0;
  let schema = await readSchema(ctx.storage);
  const from = schema?.version ?? 0;
  if (from > latest) {
    const message = `The storage schema version is ${from}, newer than this backend supports (${latest}): the data was written by a newer server version (upgrade this server, or use another DATA_DIR)`;
    if (opts.onNewer === 'warn') {
      ctx.logger.warn('storage schema is newer than this backend; serving it unchanged', { found: from, supported: latest });
      return { from, to: from, applied: [] };
    }
    throw new StorageSchemaError(message, from, latest);
  }
  const applied: AppliedMigration[] = [];
  for (const m of migrations) {
    if (m.version <= (schema?.version ?? 0)) continue;
    const t0 = Date.now();
    const changed = await m.up(ctx);
    const at = ctx.clock.now();
    const entry: AppliedMigration = { version: m.version, id: m.id, at, changed };
    schema = { version: m.version, applied: [...(schema?.applied ?? []), entry], createdAt: schema?.createdAt ?? at, updatedAt: at };
    await ctx.storage.put(META_COLLECTION, SCHEMA_DOC_ID, schema);
    applied.push(entry);
    ctx.logger.info('storage migration applied', { id: m.id, version: m.version, changed, ms: Date.now() - t0 });
  }
  if (schema === null) {
    const at = ctx.clock.now();
    schema = { version: latest, applied: [], createdAt: at, updatedAt: at };
    await ctx.storage.put(META_COLLECTION, SCHEMA_DOC_ID, schema);
  }
  return { from, to: schema.version, applied };
}
